import { useState } from "react";
import { ImageIcon, PlusIcon } from "lucide-react";
import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
} from "@/lib/Components/DataTable/DataTable";
import { ModelLinkCell } from "@/lib/Components/DataTable/Cells/ModelLinkCell";
import { DeEmphasisedCell } from "@/lib/Components/DataTable/DeEmphasisedCell";
import { SelectFilter } from "@/lib/Components/DataTable/Filters/SelectFilter";
import { Badge } from "@/components/catalyst/badge";
import { Button } from "@/components/catalyst/button";
import { ImoovaImage } from "@/app/Media/Components/Image";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { formatDate } from "@/lib/Formatters/formatDate";
import {
  BlogPostListItem,
  blogPostListQuery,
} from "@/app/Blogs/GraphQL/blogPostListQuery";
import { blogAuthorListQuery } from "@/app/Blogs/GraphQL/blogAuthorQueries";
import { blogTagListQuery } from "@/app/Blogs/GraphQL/blogTagQueries";
import { BlogPostCreateDialog } from "@/app/Blogs/Components/BlogPostCreateDialog";
import { blogAudienceLabels } from "@/app/Blogs/Components/BlogAudienceInput";
import { BlogPostStatusTabs } from "@/app/Blogs/Components/BlogPostStatusTabs";
import {
  BlogAudience,
  BlogPostStatus,
  MediaConversionType,
  QueryBlogPostsOrderByColumn,
  SortOrder,
} from "@/gql/graphql";

type BlogPostTableColumnId =
  | "title"
  | "status"
  | "audience"
  | "author"
  | "published_at"
  | "updated_at";

const sortColumns: Partial<
  Record<BlogPostTableColumnId, QueryBlogPostsOrderByColumn>
> = {
  title: QueryBlogPostsOrderByColumn.Title,
  published_at: QueryBlogPostsOrderByColumn.PublishedAt,
  updated_at: QueryBlogPostsOrderByColumn.UpdatedAt,
};

type BlogPostTableFilters = {
  status: BlogPostStatus | null;
  audience: BlogAudience | null;
  authorId: string | null;
  tagId: string | null;
};

export function BlogPostTable() {
  const [filters, setFilters] = useState<BlogPostTableFilters>({
    status: null,
    audience: null,
    authorId: null,
    tagId: null,
  });
  const [page, setPage] = useState(1);
  const { open } = useDialog(BlogPostCreateDialog);

  const updateFilters = (patch: Partial<BlogPostTableFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  };
  const { data: authorData } = useGqlQuery(blogAuthorListQuery, {});
  const { data: tagData } = useGqlQuery(blogTagListQuery, {});

  const columns: DataTableColDef<
    BlogPostListItem,
    any,
    BlogPostTableColumnId
  >[] = [
    {
      id: "title",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Title" />
      ),
      enableSorting: true,
      accessorFn: (data) => data.title,
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <div className="relative size-10 shrink-0 overflow-hidden rounded-md border border-zinc-200 bg-zinc-100">
            {row.original.hero_image ? (
              <ImoovaImage
                media={row.original.hero_image}
                conversion={MediaConversionType.Small}
                alt=""
                fill
                sizes="40px"
              />
            ) : (
              <div className="flex size-full items-center justify-center text-zinc-400">
                <ImageIcon className="size-4" />
              </div>
            )}
          </div>
          <div className="min-w-0">
            <ModelLinkCell
              from="/$tenant/$orgId/supplier/{-$supplierId}"
              to="/$tenant/$orgId/supplier/{-$supplierId}/blogs/$id"
              params={(prev) => ({ ...prev, id: row.original.id })}
              viewTransition
            >
              {row.original.title}
            </ModelLinkCell>
            <p className="truncate text-xs text-zinc-500">
              {row.original.slug}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => (
        <Badge color={row.original.is_published ? "green" : "zinc"}>
          {row.original.is_published ? "Published" : "Draft"}
        </Badge>
      ),
    },
    {
      id: "audience",
      header: "Audience",
      cell: ({ row }) => (
        <Badge
          color={
            row.original.audience === BlogAudience.Supplier ? "blue" : "zinc"
          }
        >
          {blogAudienceLabels[row.original.audience]}
        </Badge>
      ),
    },
    {
      id: "author",
      header: "Author",
      accessorFn: (data) => data.author?.name ?? "--",
      cell: DeEmphasisedCell,
    },
    {
      id: "published_at",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Published" />
      ),
      enableSorting: true,
      accessorFn: (data) =>
        data.published_at ? formatDate(data.published_at) : "--",
      cell: DeEmphasisedCell,
    },
    {
      id: "updated_at",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Updated" />
      ),
      enableSorting: true,
      accessorFn: (data) => formatDate(data.updated_at),
      cell: DeEmphasisedCell,
    },
  ];

  return (
    <div>
      <BlogPostStatusTabs
        status={filters.status}
        onStatusChange={(status) => updateFilters({ status })}
      />
      <DataTable
        id="blog-posts"
        columns={columns}
        document={blogPostListQuery}
        accessor={(data) => data.blogPosts}
        page={page}
        onPageChange={setPage}
        initialSorting={[{ id: "published_at", desc: true }]}
        getQueryVariables={({ pagination, sorting, search }) => {
          const orderBy = sorting.flatMap((sort) => {
            const column = sortColumns[sort.id];
            return column
              ? [{ column, order: sort.desc ? SortOrder.Desc : SortOrder.Asc }]
              : [];
          });

          return {
            first: pagination.pageSize,
            page: pagination.pageIndex,
            search: search || undefined,
            status: filters.status ?? undefined,
            audience: filters.audience ?? undefined,
            authorId: filters.authorId ?? undefined,
            tagId: filters.tagId ?? undefined,
            orderBy: orderBy.length ? orderBy : undefined,
          };
        }}
        filters={
          <div className="flex flex-wrap gap-2">
            <SelectFilter
              label="Audience"
              selected={filters.audience}
              options={Object.values(BlogAudience).map((value) => ({
                value,
                label: blogAudienceLabels[value],
              }))}
              onChange={(audience) => updateFilters({ audience })}
            />
            <SelectFilter
              label="Author"
              selected={filters.authorId}
              options={(authorData?.blogAuthors ?? []).map((author) => ({
                value: author.id,
                label: author.name,
              }))}
              onChange={(authorId) => updateFilters({ authorId })}
            />
            <SelectFilter
              label="Tag"
              selected={filters.tagId}
              options={(tagData?.blogTags ?? []).map((tag) => ({
                value: tag.id,
                label: tag.name,
              }))}
              onChange={(tagId) => updateFilters({ tagId })}
            />
          </div>
        }
        rightButtons={
          <Button onClick={() => open({})}>
            <PlusIcon />
            Create post
          </Button>
        }
      />
    </div>
  );
}
